import { Modal } from "flowbite-react"
import { FormikProvider, useFormik } from "formik"
import * as Yup from 'yup';
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { IVehicle, VEHICLE_STATUS } from "../../../../types/vehicle.type";
import { retrieveVehicleLicensePlate, updateVehicleOneProps } from "../../../../common/services/vehicles.api";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import InputFormik from "../../../../common/components/form/InputFormik";
import { maskCurrency, maskPhone } from "../../../../common/helpers/mask.helpers";
import { FileInputPdf } from "../../../../common/components/form/FileInputPdf";
import { useEffect, useMemo, useState } from "react";
import { useUser } from "@/common/services/users.api";

const validationSchema = (hasFinalInstallmentDate: boolean) => Yup.object().shape({
  newStatusVehicle: Yup.string().required('Campo obrigatório'),
  saleData: Yup.object().shape({
    buyerName: Yup.string().when('newStatusVehicle', {
      is: (val) => val === VEHICLE_STATUS.SELLED,
      then: (schema) => schema.required('Campo obrigatório')
    }),
    buyerPhone: Yup.string().when('newStatusVehicle', {
      is: (val) => val === VEHICLE_STATUS.SELLED,
      then: (schema) => schema.required('Campo obrigatório')
    }),
    saleValue: Yup.string().when('newStatusVehicle', {
      is: (val) => val === VEHICLE_STATUS.SELLED,
      then: (schema) => schema.required('Campo obrigatório')
    }),
    finalInstallmentDate: Yup.string().when([], {
      is: () => hasFinalInstallmentDate,
      then: (schema) => schema.required('Campo obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
  protectionLink: Yup.string().when('newStatusVehicle', {
    is: (val) => val === VEHICLE_STATUS.TRIGGER_OPENING,
    then: (schema) => schema.required('Campo obrigatório')
  }),
});

type Props = {
  vehicle: Partial<IVehicle>;
  currentPathFilesSale?: string;
  onClose: () => void;
}

export default function ModalChangeStatusVehicle(props: Props) {
  const { notifySuccess, notifyError } = useToastContext();
  const [vehicles, setVehicle] = useState<IVehicle>()
  const pathReimbursementToUpload = useMemo(() => `vehicles/${props.vehicle._id}/ressarcimento`, [props.vehicle._id])
  const [hasFinalInstallmentDate, setHasFinalInstallmentDate] = useState(false);
  const user = useUser();

  useEffect(() => {
    async function loadVehicles(licensePlate: string) {
      if (!licensePlate) return;
      const result = await retrieveVehicleLicensePlate(props.vehicle.licensePlate)
      setVehicle(result.data)
    }
    if (props.vehicle.licensePlate) {
      loadVehicles(props.vehicle.licensePlate)
    }
  }, [props.vehicle])

  const vehicleId = props.vehicle._id ? props.vehicle._id : vehicles?._id
  const saveFilesSale = async (filesSale,
  ) => {
    if (!filesSale)
      return notifyError("Nenhum arquivo enviado");

    try {
      const result = await updateVehicleOneProps(vehicleId!, {
        filesSale,
      })

      if (!result.ok)
        return notifyError(result.message || "Erro ao enviar ressarcimento");

      props.vehicle.pathReimbursement = filesSale || props.vehicle.pathReimbursement;
      notifySuccess("Ressarcimento enviado com sucesso");
    }
    catch (error) {
      notifyError("Erro ao enviar ressarcimento");
      return;
    }
  }

  const formik = useFormik({
    initialValues: {
      newStatusVehicle: '',
      saleData: {
        buyerName: '',
        buyerPhone: '',
        saleValue: '',
        finalInstallmentDate: null,
        protectionLink: '',
        pathReimbursement: ''
      }
    },

    validationSchema: validationSchema,
    onSubmit: async (data) => {

      const saleDataValue = data.saleData.saleValue ? parseFloat(data.saleData.saleValue.replace(/\D/g, "")) / 100 : 0
      const vehicleData = {
        _id: vehicleId!,
        status: data.newStatusVehicle as VEHICLE_STATUS,
        saleData: {
          buyerName: data.saleData.buyerName,
          buyerPhone: data.saleData.buyerPhone,
          saleValue: saleDataValue,
          protectionLink: data["protectionLink"] || '',
          pathReimbursement: data.saleData.pathReimbursement || '',
          finalInstallmentDate: data.saleData.finalInstallmentDate ?? null,
        }
      }

      const result = await updateVehicleOneProps(vehicleData._id, vehicleData)
      if (!result.ok) {
        notifyError((result as { message?: string })?.message || "unknown error")
        return
      }
      notifySuccess("Status do veículo atualizado com sucesso")
    }
  })

  type Option = { value: string; label: string };

  const DASH_OPTION: Option = { value: "", label: "-" };

  //TODO: Mover para obter do Auth0
  const ADMIN_EMAILS: string[] = [
    "wandson.helber@gmail.com",
    "rrochaalcantara@gmail.com",
    "giovanna.locavibe@gmail.com"
  ].map(e => e.toLowerCase());

  const isAdmin = user?.email ? ADMIN_EMAILS.includes(user.email.toLowerCase()) : false;

  const ALL_STATUS_OPTIONS: Option[] = [
    { value: VEHICLE_STATUS.THIRD_PARTY_INSURANCE, label: "Acionamento de terceiros" },
    { value: VEHICLE_STATUS.INSURANCE, label: "Abertura de acionamento" },
    { value: VEHICLE_STATUS.REIMBURSEMENT, label: "Aguard. Ressarcimento" },
    { value: VEHICLE_STATUS.AWAITING_TRANSFER, label: "Aguardando Transferência" },
    { value: VEHICLE_STATUS.RENTED, label: "Alugado" },
    { value: VEHICLE_STATUS.IMPOUNDED, label: "Apreendido" },
    { value: VEHICLE_STATUS.MISAPPROPRIATION, label: "Apropriação indébita" },
    { value: VEHICLE_STATUS.AVAILABLE, label: "Disponível" },
    { value: VEHICLE_STATUS.IN_REPAIR, label: "Em manutenção" },
    { value: VEHICLE_STATUS.UNDER_PREPARATION, label: "Em preparação" },
    { value: VEHICLE_STATUS.IN_READY_RESPONSE, label: "Em pronta resposta" },
    { value: VEHICLE_STATUS.UNDER_REVIEW, label: "Em revisão" },
    { value: VEHICLE_STATUS.IN_TRANSIT, label: "Em trânsito" },
    { value: VEHICLE_STATUS.THEFT_ROBBERY, label: "Furto/Roubo" },
    { value: VEHICLE_STATUS.PREPARATION_FOR_SALE, label: "Motos para Venda" },
    { value: VEHICLE_STATUS.TRIGGER_OPENING, label: "Proteção acionada" },
    { value: VEHICLE_STATUS.IN_REVISION, label: "Reparo estético" },
    { value: VEHICLE_STATUS.TRIAGE, label: "Triagem" },
    { value: VEHICLE_STATUS.SELLED, label: "Veículo vendido" },
  ];

  const toDash = (opts: Option[]): Option[] => [DASH_OPTION, ...opts];
  const toOpt = (value: string, label: string): Option => ({ value, label });

  const TRANSITIONS: Partial<Record<VEHICLE_STATUS, Option[]>> = {
    [VEHICLE_STATUS.UNDER_PREPARATION]: toDash([
      toOpt(VEHICLE_STATUS.AVAILABLE, "Disponível"),
    ]),
    [VEHICLE_STATUS.SELLED]: toDash([
      toOpt(VEHICLE_STATUS.AWAITING_TRANSFER, "Aguardando Transferência"),
    ]),
    [VEHICLE_STATUS.AVAILABLE]: toDash([
      toOpt(VEHICLE_STATUS.TRIAGE, "Triagem"),
    ]),

    [VEHICLE_STATUS.RENTED]: toDash([
      toOpt(VEHICLE_STATUS.IMPOUNDED, "Apreendido"),
      toOpt(VEHICLE_STATUS.MISAPPROPRIATION, "Apropriação indébita"),
      toOpt(VEHICLE_STATUS.IN_READY_RESPONSE, "Em pronta resposta"),
    ]),

    [VEHICLE_STATUS.TRIAGE]: toDash([
      toOpt(VEHICLE_STATUS.INSURANCE, "Abertura de acionamento"),
      toOpt(VEHICLE_STATUS.THIRD_PARTY_INSURANCE, "Acionamento de terceiros"),
      toOpt(VEHICLE_STATUS.IN_REPAIR, "Em manutenção"),
      toOpt(VEHICLE_STATUS.IN_REVISION, "Reparo estético"),
      toOpt(VEHICLE_STATUS.PREPARATION_FOR_SALE, "Motos para Venda"),
    ]),

    [VEHICLE_STATUS.INSURANCE]: toDash([
      toOpt(VEHICLE_STATUS.TRIGGER_OPENING, "Proteção acionada"),
    ]),

    [VEHICLE_STATUS.TRIGGER_OPENING]: toDash([
      toOpt(VEHICLE_STATUS.IN_REVISION, "Reparo estético"),
      toOpt(VEHICLE_STATUS.REIMBURSEMENT, "Aguard. Ressarcimento"),
    ]),

    [VEHICLE_STATUS.IN_REPAIR]: toDash([
      toOpt(VEHICLE_STATUS.AVAILABLE, "Disponível"),
      toOpt(VEHICLE_STATUS.IN_REVISION, "Reparo estético"),
      toOpt(VEHICLE_STATUS.INSURANCE, "Abertura de acionamento"),
    ]),

    [VEHICLE_STATUS.UNDER_REVIEW]: toDash([
      toOpt(VEHICLE_STATUS.AVAILABLE, "Disponível"),
      toOpt(VEHICLE_STATUS.IN_REVISION, "Reparo estético"),
    ]),

    [VEHICLE_STATUS.IN_REVISION]: toDash([
      toOpt(VEHICLE_STATUS.AVAILABLE, "Disponível"),
      toOpt(VEHICLE_STATUS.IN_REPAIR, "Em manutenção"),
    ]),

    [VEHICLE_STATUS.PREPARATION_FOR_SALE]: toDash([
      toOpt(VEHICLE_STATUS.SELLED, "Veículo vendido"),
    ]),

    [VEHICLE_STATUS.REIMBURSEMENT]: toDash([
      toOpt(VEHICLE_STATUS.SELLED, "Veículo vendido"),
    ]),

    [VEHICLE_STATUS.MISAPPROPRIATION]: toDash([
      toOpt(VEHICLE_STATUS.TRIGGER_OPENING, "Proteção acionada"),
    ]),

    [VEHICLE_STATUS.IN_READY_RESPONSE]: toDash([
      toOpt(VEHICLE_STATUS.TRIAGE, "Triagem"),
      toOpt(VEHICLE_STATUS.TRIGGER_OPENING, "Proteção acionada"),
    ]),

    [VEHICLE_STATUS.IMPOUNDED]: toDash([
      toOpt(VEHICLE_STATUS.TRIAGE, "Triagem"),
    ]),
  };

  const currentStatus = props.vehicle.status ?? vehicles?.status;
  const statusOptions: Option[] = isAdmin
    ? toDash(ALL_STATUS_OPTIONS)
    : (currentStatus ? TRANSITIONS[currentStatus] ?? [DASH_OPTION] : [DASH_OPTION]);

  const handleCloseModal = () => { props.onClose() }
  return (
    <Modal
      size={'3xl'}
      className='relative'
      show={true}
      onClose={() => handleCloseModal()}
    >
      <form onSubmit={formik.handleSubmit}>
        <FormikProvider value={formik}>
          <Modal.Header className="text-black">
            <h2 className="dark:text-black">Alterar status da moto:  {props.vehicle.licensePlate}</h2>
          </Modal.Header>
          <Modal.Body className="text-black">
            <div>
              <ComboBoxFormik
                className="mt-4"
                label="Selecione o novo status da moto:"
                name="newStatusVehicle"
                options={statusOptions}
                required={true}
              />
            </div>


            {formik.values.newStatusVehicle === VEHICLE_STATUS.SELLED && (
              <div>
                <InputFormik
                  className="mt-2"
                  label="Nome do comprador:"
                  name="saleData.buyerName"
                  required={true}
                />
                <InputFormik
                  label="Telefone do comprador:"
                  name="saleData.buyerPhone"
                  className="col-span-1 mt-2"
                  required={true}
                  mask={maskPhone}
                />
                <InputFormik
                  className="mt-2"
                  label="Valor da venda:"
                  name="saleData.saleValue"
                  required={true}
                  mask={maskCurrency}
                />
                <div className="mt-4 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={hasFinalInstallmentDate}
                    onChange={(e) =>
                      setHasFinalInstallmentDate(e.target.checked)
                    }
                  />

                  <label htmlFor="informarDataFinal" className="text-sm">
                    Venda parcelada?
                  </label>
                </div>

                {hasFinalInstallmentDate && (
                  <InputFormik
                    className="mt-2 mb-2"
                    type="date"
                    name="saleData.finalInstallmentDate"
                    label="Data final do parcelamento:"
                    required={true}
                  />
                )}

                <FileInputPdf
                  onChange={saveFilesSale}
                  pathToUpload={pathReimbursementToUpload}
                  currentPath={props.currentPathFilesSale || ""}
                  nameFile="Enviar anexos da vendas"
                />
              </div>
            )}

            {formik.values.newStatusVehicle === VEHICLE_STATUS.INSURANCE &&
              <div className="font-semibold">
                <p className="mt-4">Para acionar a proteção, acesse o link: <a className="text-redPrimary" href="https://motoclub.ileva.com.br/comunicado/acionamento-de-protecao" target="_blank" rel="noreferrer">Acionamento de proteção</a></p>
              </div>
            }
            {formik.values.newStatusVehicle === VEHICLE_STATUS.TRIGGER_OPENING &&
              <InputFormik className="mt-2"
                label="Informe o link do protocolo:"
                name="protectionLink"
                required={true}
              />
            }

            {(formik.values.newStatusVehicle === VEHICLE_STATUS.MISAPPROPRIATION ||
              formik.values.newStatusVehicle === VEHICLE_STATUS.IN_READY_RESPONSE) && (<div>
                <p>Boletim de ocorrência <span className="text-red-500 font-bold">*</span></p>
                <FileInputPdf
                  onChange={(file) => formik.setFieldValue("boletimFile", file)}
                  pathToUpload={pathReimbursementToUpload}
                  currentPath={props.currentPathFilesSale || ""}
                  nameFile="boletim de ocorrência"
                />
              </div>
              )}

            {(formik.values.newStatusVehicle === VEHICLE_STATUS.REIMBURSEMENT || props.vehicle.status === VEHICLE_STATUS.REIMBURSEMENT) && (
              <FileInputPdf
                name="pathReimbursement"
                onChange={saveFilesSale}
                pathToUpload={pathReimbursementToUpload}
                currentPath={props.vehicle.pathReimbursement || ""}
                nameFile="Enviar Ressarcimento"
              />
            )}

          </Modal.Body>
          <Modal.Footer className="justify-end h-16 pb-8" >
            <ButtonWithLoader
              type="button" isLoading={formik.isSubmitting} disabled={!formik.isValid} onClick={formik.handleSubmit}>
              Salvar
            </ButtonWithLoader>
          </Modal.Footer>
        </FormikProvider>
      </form>

    </Modal>
  )
}
